import { DragEndEvent, DragMoveEvent, DragStartEvent } from "@dnd-kit/core";
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

interface BlockDragContextValue {
  activeId: string | null;
  overId: string | null;
  overIdRef: React.MutableRefObject<string | null>;
  initialCursorY: React.MutableRefObject<number>;
  onDragStart: (e: DragStartEvent) => void;
  onDragMove: (e: DragMoveEvent, blockIds: string[], endZoneId: string) => void;
  onDragEnd: (
    e: DragEndEvent,
    blockIds: string[],
    moveBlock: (args: { firstIndex: number; secondIndex: number }) => void,
    endZoneId: string,
    blockCount: number,
  ) => void;
  onDragCancel: () => void;
}

const BlockDragContext = createContext<BlockDragContextValue | null>(null);

export const BlockDragProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverIdState] = useState<string | null>(null);
  // Ref so onDragEnd always reads the latest value regardless of React batching
  const overIdRef = useRef<string | null>(null);
  const initialCursorY = useRef(0);

  const setOverId = useCallback((id: string | null) => {
    overIdRef.current = id;
    setOverIdState(id);
  }, []);

  const onDragStart = useCallback((e: DragStartEvent) => {
    const id = e.active.id as string;
    setActiveId(id);
    setOverId(id);
    const event = e.activatorEvent;
    if ("clientY" in event) {
      initialCursorY.current = (event as PointerEvent).clientY;
    }
  }, [setOverId]);

  const onDragMove = useCallback((e: DragMoveEvent, blockIds: string[], endZoneId: string) => {
    const rawId = (e.over?.id as string) ?? null;
    if (!rawId) return;
    if (rawId === endZoneId) {
      setOverId(endZoneId);
      return;
    }
    const blockIdx = blockIds.indexOf(rawId);
    if (blockIdx === -1) return;

    const cursorY = initialCursorY.current + e.delta.y;
    const overRect = e.over!.rect;
    const overMidY = overRect.top + overRect.height / 2;

    if (cursorY > overMidY) {
      setOverId(blockIdx < blockIds.length - 1 ? blockIds[blockIdx + 1] : endZoneId);
    } else {
      setOverId(rawId);
    }
  }, [setOverId]);

  const onDragEnd = useCallback((
    e: DragEndEvent,
    blockIds: string[],
    moveBlock: (args: { firstIndex: number; secondIndex: number }) => void,
    endZoneId: string,
    blockCount: number,
  ) => {
    const draggedId = e.active?.id as string;
    const currentOverId = overIdRef.current;
    setActiveId(null);
    setOverId(null);
    if (!draggedId || !currentOverId || draggedId === currentOverId) return;
    const firstIndex = blockIds.indexOf(draggedId);
    if (firstIndex === -1) return;
    if (currentOverId === endZoneId) {
      if (firstIndex === blockCount - 1) return;
      moveBlock({ firstIndex, secondIndex: blockCount - 1 });
      return;
    }
    const secondIndex = blockIds.indexOf(currentOverId);
    if (secondIndex === -1) return;
    const adjustedSecond = secondIndex > firstIndex ? secondIndex - 1 : secondIndex;
    moveBlock({ firstIndex, secondIndex: adjustedSecond });
  }, [setOverId]);

  const onDragCancel = useCallback(() => {
    setActiveId(null);
    setOverId(null);
  }, [setOverId]);

  const value = useMemo(() => ({
    activeId,
    overId,
    overIdRef,
    initialCursorY,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragCancel,
  }), [activeId, overId, onDragStart, onDragMove, onDragEnd, onDragCancel]);

  return (
    <BlockDragContext.Provider value={value}>
      {children}
    </BlockDragContext.Provider>
  );
};

export const useBlockDrag = (): BlockDragContextValue => {
  const ctx = useContext(BlockDragContext);
  if (!ctx) throw new Error("useBlockDrag must be used within a BlockDragProvider");
  return ctx;
};
